import { createRouter, createWebHistory } from 'vue-router'
import { ensureMe } from '@/services/auth'
import { ensureDeploymentMode } from '@/composables/useDeploymentMode'
import { isAreaMismatch } from './areaGuard'
import ExpectationsFramework from '@/views/ExpectationsFramework.vue'
import UpcomingEvents        from '@/views/UpcomingEvents.vue'
import Logs                  from '@/views/Logs.vue'
import LoginView             from '@/views/LoginView.vue'
import ForbiddenView         from '@/views/ForbiddenView.vue'
import LocalOnlyView         from '@/views/LocalOnlyView.vue'

const routes = [
  // ── Public (internal) ────────────────────────────────────────────────
  {
    path: '/login',
    name: 'Login',
    component: LoginView,
    meta: { public: true },
  },

  // Old bookmarks/links to the former separate Reader-Portal login page -
  // there is now only the one login page above, which handles both
  // internal and Reader-Portal accounts (see LoginView.vue's handleLogin()).
  {
    path: '/portal-login',
    redirect: (to) => ({ path: '/login', query: to.query }),
  },

  // ── Public (reader portal) ───────────────────────────────────────────
  // None of these carry requiresAuth/area - they are the entry points
  // reached BEFORE a session exists (signup link from an email, forgot-
  // password, etc.).
  {
    path: '/signup',
    name: 'signup',
    component: () => import('@/views/portal/SignupView.vue'),
    meta: { public: true },
  },
  {
    path: '/register',
    name: 'register',
    component: () => import('@/views/portal/RegisterView.vue'),
    meta: { public: true },
  },
  {
    // Landing target for the verification mail link (see portal.py
    // portal_register: _frontend_url(f"/verify?t={raw_token}")). Never
    // guarded - a visitor here has no session yet by definition.
    path: '/verify',
    name: 'verify',
    component: () => import('@/views/portal/VerifyView.vue'),
    meta: { public: true },
  },
  {
    path: '/forgot-password',
    name: 'forgot-password',
    component: () => import('@/views/portal/ForgotPasswordView.vue'),
    meta: { public: true },
  },
  {
    path: '/reset-password',
    name: 'reset-password',
    component: () => import('@/views/portal/ResetPasswordView.vue'),
    meta: { public: true },
  },

  // ── Rechtliche Seiten (Sammelauftrag Punkte 1+2) ─────────────────────
  // Oeffentlich erreichbar (kein Login noetig) wie bei anderen Rechtsseiten
  // ueblich, und weil RegisterView.vue schon vor dem Login darauf verlinkt.
  {
    path: '/datenschutz',
    name: 'privacy-policy',
    component: () => import('@/views/legal/PrivacyPolicyView.vue'),
    meta: { public: true },
  },
  {
    path: '/disclaimer',
    name: 'disclaimer',
    component: () => import('@/views/legal/DisclaimerView.vue'),
    meta: { public: true },
  },

  // ── Redirect ──────────────────────────────────────────────────────────
  { path: '/', redirect: '/expectations-framework' },

  // ── All authenticated internal users ───────────────────────────────────
  {
    path: '/expectations-framework',
    name: 'ExpectationsFramework',
    component: ExpectationsFramework,
    // localOnly (Portal-Administration auf dem VPS): /analyze*, /charts/*,
    // /recalculate/* sind auf dem VPS serverseitig blockiert (app.py,
    // MERIDIAN_DEPLOYMENT=portal) - siehe Guard unten.
    meta: { requiresAuth: true, area: 'internal', page: 'analyze', localOnly: true },
  },
  {
    path: '/upcoming-events',
    name: 'UpcomingEvents',
    component: UpcomingEvents,
    // localOnly: /events* ist Klasse C (interner Event-Workflow, siehe
    // Abschlussbericht) und auf dem VPS serverseitig blockiert.
    meta: { requiresAuth: true, area: 'internal', page: 'events', localOnly: true },
  },
  {
    path: '/logs',
    name: 'Logs',
    component: Logs,
    // No page_key exists for this in app_pages - ungated for any internal
    // user, same as before this auftrag (see page_access catalog, migration
    // 0004_reader_portal.sql).
    meta: { requiresAuth: true, area: 'internal' },
  },
  {
    path: '/watchlist',
    name: 'Watchlist',
    component: () => import('@/views/WatchlistView.vue'),
    // localOnly: /api/watchlist*, /api/assignments*, /api/search,
    // /api/instruments/* (Watchlist-Suche, Refinitiv-backed, siehe
    // CoverageModal.vue) sind alle Klasse C und auf dem VPS serverseitig
    // blockiert.
    meta: { requiresAuth: true, area: 'internal', page: 'watchlist', localOnly: true },
  },
  {
    path: '/research/:ric',
    component: () => import('@/views/research/ResearchProcess.vue'),
    props: true,
    // localOnly: /research/* ist Klasse C (interner Research-Workflow).
    meta: { requiresAuth: true, area: 'internal', page: 'research', localOnly: true },
    children: [
      { path: '',                    redirect: to => `/research/${to.params.ric}/overview` },
      { path: 'overview',            name: 'research-overview',   component: () => import('@/views/research/steps/OverviewStep.vue') },
      { path: 'financial-model',     name: 'research-model',      component: () => import('@/views/research/steps/FinancialModelStep.vue') },
      { path: 'business',            name: 'research-business',   component: () => import('@/views/research/steps/BusinessManagementStep.vue') },
      { path: 'market',              name: 'research-market',     component: () => import('@/views/research/steps/MarketStep.vue') },
      { path: 'peer-group',          name: 'research-peers',      component: () => import('@/views/research/steps/PeerGroupStep.vue') },
      { path: 'alt-data',            name: 'research-altdata',    component: () => import('@/views/research/steps/AltDataStep.vue') },
      { path: 'guidance-risk',       name: 'research-risk',       component: () => import('@/views/research/steps/GuidanceRiskStep.vue') },
      { path: 'valuation',           name: 'research-valuation',  component: () => import('@/views/research/steps/ValuationStep.vue') },
      { path: 'variant-perception',  name: 'research-variant',    component: () => import('@/views/research/steps/VariantPerceptionStep.vue') },
      { path: 'decision',            name: 'research-decision',   component: () => import('@/views/research/steps/DecisionStep.vue') },
      { path: 'report',              name: 'research-report',     component: () => import('@/views/research/steps/ReportStep.vue') },
      { path: 'monitoring',          name: 'research-monitoring', component: () => import('@/views/research/steps/MonitoringStep.vue') },
    ],
  },

  // ── Admin + Portfolio Manager ──────────────────────────────────────────
  {
    path: '/control',
    name: 'ControlCenter',
    component: () => import('@/views/ControlCenter.vue'),
    // localOnly: Control Center steuert token_server/control_server, die auf
    // dem VPS nicht existieren (siehe docs/architecture-decision-push-model.md).
    meta: {
      requiresAuth: true,
      area: 'internal',
      roles: ['admin', 'portfolio_manager'],
      page: 'control_center',
      title: 'Meridian Control Center',
      localOnly: true,
    },
  },

  // ── Admin only ─────────────────────────────────────────────────────────
  {
    path: '/user-management',
    name: 'UserManagement',
    component: () => import('@/views/UserManagement.vue'),
    meta: {
      requiresAuth: true,
      area: 'internal',
      roles: ['admin'],
      page: 'user_management',
    },
  },
  {
    path: '/admin/reports',
    name: 'ReportAdmin',
    component: () => import('@/views/admin/ReportAdminView.vue'),
    meta: {
      requiresAuth: true,
      area: 'internal',
      roles: ['admin'],
      // No real page: 'report_admin' key exists in app_pages - the backend
      // endpoints (/api/reports*, /api/company-logos*) check roles=['admin']
      // only, no page= (see app.py). Sicherheits-Audit finding: a page key
      // here would be pointless AND misleading, since grant_all_internal_pages()
      // hands every new internal page_key to every internal user regardless
      // of role (a known, not-yet-fixed gap) - `roles` above is the only
      // check that actually narrows anything, both here and server-side.
      page: 'report_admin',
    },
  },

  {
    path: '/admin/universe',
    name: 'Universe',
    component: () => import('@/views/admin/UniverseView.vue'),
    meta: {
      requiresAuth: true,
      area: 'internal',
      roles: ['admin'],
      // Wie bei /admin/reports: kein echter page_key - 'roles' ist die einzige Einschraenkung im Frontend, die echte Grenze ist
      // @auth.require_auth(["admin"]) auf jedem /api/universe*-Endpunkt (universe.py: 401 ohne Session, 403 fuer Nicht-Admins).
      // localOnly: im reinen Portal-Modus (MERIDIAN_DEPLOYMENT=portal) sind /api/universe* serverseitig 404-geblockt (app.py).
      localOnly: true,
      title: 'Universe',
    },
  },

  // ── Reader portal ────────────────────────────────────────────────────
  // Dashboard is the portal's start page (see PORTAL_NAV / Auftrag Teil 3.2).
  {
    path: '/portal',
    component: () => import('@/views/portal/PortalLayout.vue'),
    meta: { requiresAuth: true, area: 'portal' },
    children: [
      { path: '',                    redirect: '/portal/dashboard' },
      {
        path: 'dashboard',
        name: 'portal-dashboard',
        component: () => import('@/views/portal/PortalDashboardView.vue'),
        meta: { page: 'portal.dashboard' },
      },
      {
        path: 'equity-research',
        name: 'portal-research',
        component: () => import('@/views/portal/EquityResearchView.vue'),
        meta: { page: 'portal.equity_research' },
      },
      {
        path: 'equity-research/:id',
        name: 'portal-research-detail',
        component: () => import('@/views/portal/EquityResearchView.vue'),
        props: true,
        meta: { page: 'portal.equity_research' },
      },
      {
        path: 'equity-watchlist',
        name: 'portal-watchlist',
        component: () => import('@/views/portal/EquityWatchlistView.vue'),
        meta: { page: 'portal.equity_watchlist' },
      },
      {
        path: 'market-reports',
        name: 'portal-market',
        component: () => import('@/views/portal/MarketReportsView.vue'),
        meta: { page: 'portal.market_reports' },
      },
      {
        path: 'market-reports/:id',
        name: 'portal-market-detail',
        component: () => import('@/views/portal/MarketReportsView.vue'),
        props: true,
        meta: { page: 'portal.market_reports' },
      },
      {
        path: 'macro',
        name: 'portal-macro',
        component: () => import('@/views/portal/MacroDashboardView.vue'),
        // Sammelauftrag Punkt 4: Reader duerfen diese Ansicht nicht mehr
        // sehen, unabhaengig von jeder page_keys-Vergabe (blockRoles greift
        // VOR der page-Pruefung unten) - Backend spiegelt das in
        // portal.py:portal_macro() (kein allow_external mehr).
        meta: { page: 'portal.macro', blockRoles: ['reader'] },
      },
      {
        path: 'account',
        name: 'portal-account',
        component: () => import('@/views/portal/AccountView.vue'),
      },
    ],
  },

  // ── Shared ───────────────────────────────────────────────────────────
  {
    path: '/forbidden',
    name: 'forbidden',
    component: ForbiddenView,
    // Reachable by any authenticated user (internal or external) - no
    // `area`, so neither of the area checks below redirects away from it.
    meta: { requiresAuth: true },
  },
  {
    // Portal-Administration auf dem VPS: Ziel für jede meta.localOnly-Route,
    // wenn deployment === 'portal' (siehe Guard unten) - zeigt eine saubere
    // Meldung statt eines rohen 404/Netzwerkfehlers der eigentlichen View.
    path: '/local-only',
    name: 'local-only',
    component: LocalOnlyView,
    meta: { requiresAuth: true },
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
})

// This guard is a UX convenience, nothing more: it stops a user from
// landing in an empty or broken view. It is NOT the access-control
// boundary — every endpoint enforces its own 401 (no/invalid session) or
// 403 (authenticated but forbidden) independently of whatever this guard
// decides, and would refuse the request even if this guard were removed
// entirely or bypassed.
router.beforeEach(async (to) => {
  if (to.meta.public) return true
  if (!to.meta.requiresAuth) return true

  const me = await ensureMe()
  if (!me) {
    // One login page for both areas now (see LoginView.vue) - no more
    // area-based split here.
    return { name: 'Login', query: { redirect: to.fullPath } }
  }

  // Bereichs-Pruefung in BEIDE Richtungen (siehe router/areaGuard.js fuer
  // die volle Begruendung): Reader duerfen den internen Bereich nicht
  // betreten (bestand bereits), UND internes Personal (admin/pm/analyst)
  // wird aus dem Portal-Bereich zurueckgeholt (Bug-Fund: fehlte bisher -
  // ein Admin, der z.B. ueber einen alten ?redirect=-Query-Param, einen
  // Bookmark oder manuelle Eingabe auf einer /portal/*-Route landete, blieb
  // dort haengen - App.vue waehlt das Layout rein nach route.meta.area,
  // PortalLayout.vue filtert sein Menue nur nach page_keys, die ein Admin
  // fuer "portal.*" nie hat - sichtbar blieb nur der ungefilterte
  // "Konto"-Link, alle internen Reiter verschwanden). 'portal-account' ist
  // ausgenommen - einzige Profilseite der App, kein internes Pendant.
  if (isAreaMismatch({ isExternal: me.is_external, area: to.meta.area, routeName: to.name })) {
    // "/" fuer internes Personal: identisch zum bestehenden Login-Flow
    // (LoginView.vue leitet bei Erfolg ebenfalls auf "/"), kein geratener
    // zweiter Default-Pfad. 'portal-dashboard' fuer Reader: unveraendert.
    return me.is_external ? { name: 'portal-dashboard' } : { path: '/' }
  }

  // Sammelauftrag Punkt 4: harte Rollensperre VOR der page_keys-Pruefung,
  // gilt auch fuer Admins (anders als roles/page unten) - z.B. Macro
  // Dashboard darf fuer Reader unter keinen Umstaenden erreichbar sein,
  // unabhaengig von jeder Seitenberechtigung.
  if (to.meta.blockRoles && to.meta.blockRoles.includes(me.role)) {
    return { name: 'forbidden' }
  }

  // Interne Nutzer: Rollen- und Seitenberechtigung pruefen. Admins umgehen
  // beides serverseitig ohnehin (auth.require_auth) - hier gespiegelt,
  // damit ein Admin sich nicht selbst aussperren kann.
  if (to.meta.roles && me.role !== 'admin' && !to.meta.roles.includes(me.role)) {
    return { name: 'forbidden' }
  }
  if (to.meta.page && me.role !== 'admin' && !(me.page_keys || []).includes(to.meta.page)) {
    return { name: 'forbidden' }
  }

  // Portal-Administration auf dem VPS: localOnly-Routen (Analyse, Charts,
  // Watchlist-Suche, Control Center, Research) serverseitig per
  // MERIDIAN_DEPLOYMENT=portal blockiert (404, siehe app.py) - hier bereits
  // clientseitig abgefangen, damit nicht erst ein fehlgeschlagener Request
  // in der jeweiligen View einen rohen Fehler produziert.
  if (to.meta.localOnly) {
    const mode = await ensureDeploymentMode()
    if (mode === 'portal') {
      return { name: 'local-only' }
    }
  }

  return true
})

export default router
